import React, { useState, useEffect } from 'react';
import { ArrowLeft, Wifi, Smartphone, CheckCircle, Copy, Share, Download, Home, Globe } from 'lucide-react';

// Supplier border colours — shown in UAT and Staging only
const _viteMode: string = (import.meta as any).env?.MODE ?? 'production';
const _viteNodeEnv: string = (import.meta as any).env?.VITE_NODE_ENV ?? '';
const isUatOrStaging = _viteMode !== 'production' || _viteNodeEnv === 'staging';
const SUPPLIER_BORDER: Record<string, string> = {
  FLASH:      '2px solid #22c55e',  // green-500
  MOBILEMART: '2px solid #3b82f6', // blue-500
};
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { ErrorModal } from '../ui/ErrorModal';
import { RestrictedWalletCreditModal } from '../RestrictedWalletCreditModal';
import { parseKycTierError } from '../../utils/kycTierErrors';
import { BeneficiaryList } from './shared/BeneficiaryList';
import { BeneficiaryModal } from './shared/BeneficiaryModal';
import { AddAdditionalNumberModal } from './shared/AddAdditionalNumberModal';
import { AccountSelectorModal } from './shared/AccountSelectorModal';
import { ConfirmationModal } from './shared/ConfirmationModal';
import { PaymentProceedModal, type PaymentProceedPayload } from './shared/PaymentProceedModal';
import { randsToCents } from '../../utils/formatCurrency';
import { 
  beneficiaryService, 
  airtimeDataService, 
  generateIdempotencyKey,
  formatCurrency,
  validateMobileNumber,
  type Beneficiary,
  type AirtimeDataCatalog,
  type AirtimeDataProduct,
  type PurchaseResult
} from '../../services/overlayService';
import { apiService } from '../../services/apiService';
import { beneficiaryService as centralizedBeneficiaryService } from '../../services/beneficiaryService';
import { unifiedBeneficiaryService } from '../../services/unifiedBeneficiaryService';
import { GlobalPinModal } from './shared/GlobalPinModal';
import { getNetworkIcon, getCategoryIcon } from '../ui/NetworkIcons';
import { BrandSpinner } from '../common/LoadingSpinner';
import {
  normalizeNetworkName,
  getNetworkFromMsisdn,
  extractProductNetwork,
  getMsisdnNetworkMismatch,
  getBeneficiaryNetworkLabelWarning,
} from '../../utils/networkFromMsisdn';

function getOverlayBeneficiaryMsisdn(beneficiary: Beneficiary | null): string {
  return beneficiary?.identifier || '';
}

interface AirtimeDataBeneficiary extends Beneficiary {
  // Uses accountType from base Beneficiary interface
}

type Step = 'beneficiary' | 'loading' | 'catalog' | 'confirm';
type LoadingState = 'idle' | 'loading' | 'success' | 'error';

export function AirtimeDataOverlay() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('beneficiary');
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<Beneficiary | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<AirtimeDataProduct | null>(null);
  const [catalog, setCatalog] = useState<AirtimeDataCatalog | null>(null);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  // Universal pre-debit proceed confirmation (replaces the legacy ConfirmSheet step).
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [proceedPayload, setProceedPayload] = useState<PaymentProceedPayload | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionRef, setTransactionRef] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const [restrictedModalMessage, setRestrictedModalMessage] = useState<string | undefined>(undefined);
  const [errorModalTitle, setErrorModalTitle] = useState<string>('Purchase Failed');
  const [errorModalMessage, setErrorModalMessage] = useState<string>('');
  const [errorModalType, setErrorModalType] = useState<'error' | 'warning' | 'info'>('error');
  const [alternativeProduct, setAlternativeProduct] = useState<any>(null);
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [showAddNumberModal, setShowAddNumberModal] = useState(false);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [pendingBeneficiary, setPendingBeneficiary] = useState<Beneficiary | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [beneficiaryToRemove, setBeneficiaryToRemove] = useState<Beneficiary | null>(null);
  const [beneficiaryIsMyMoolahUser, setBeneficiaryIsMyMoolahUser] = useState(false);
  const [globalPinProducts, setGlobalPinProducts] = useState<any[]>([]);
  const [showGlobalPinModal, setShowGlobalPinModal] = useState(false);
  const [eeziPinProducts, setEeziPinProducts] = useState<any[]>([]);
  const [showEeziPinModal, setShowEeziPinModal] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState<Beneficiary | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [inlineAmountInput, setInlineAmountInput] = useState<string>('');
  const [showSendToNewRecipient, setShowSendToNewRecipient] = useState(false);
  const [newRecipientPhone, setNewRecipientPhone] = useState<string>('');
  const [newRecipientName, setNewRecipientName] = useState<string>('');
  const [showSaveRecipientPrompt, setShowSaveRecipientPrompt] = useState(false);
  const [lastTransactionBeneficiary, setLastTransactionBeneficiary] = useState<{ name: string; phone: string; network?: string } | null>(null);
  const [networkPrefixWarning, setNetworkPrefixWarning] = useState<string | null>(null);

  const showNetworkMismatchModal = (message: string) => {
    setErrorModalTitle('Network mismatch');
    setErrorModalMessage(message);
    setErrorModalType('warning');
    setShowErrorModal(true);
  };

  const assertProductMatchesBeneficiaryNetwork = (beneficiary: Beneficiary | null, product: AirtimeDataProduct): boolean => {
    if (!beneficiary) return true;
    const mismatch = getMsisdnNetworkMismatch(
      getOverlayBeneficiaryMsisdn(beneficiary),
      extractProductNetwork(product)
    );
    if (mismatch.mismatch && mismatch.message) {
      showNetworkMismatchModal(mismatch.message);
      return false;
    }
    return true;
  };

  // Load beneficiaries and PIN products on mount
  useEffect(() => {
    loadBeneficiaries();

    // Pre-load International Airtime (global PIN) products
    apiService.getAirtimeDataProducts()
      .then(({ globalPin }) => setGlobalPinProducts(globalPin))
      .catch(() => {/* silently ignore */});

    // Pre-load eeziAirtime Token products.
    // These are currently stored as vasType='airtime' in the DB (until next catalog sync
    // reclassifies them as 'voucher'). Search both vasTypes to be resilient.
    Promise.all([
      apiService.compareSuppliers('airtime').catch(() => ({ bestDeals: [], products: [] })),
      apiService.compareSuppliers('voucher').catch(() => ({ bestDeals: [], products: [] })),
    ]).then(([airtimeComp, voucherComp]: [any, any]) => {
        const fromAirtime = (airtimeComp?.bestDeals?.length > 0 ? airtimeComp.bestDeals : null) || airtimeComp?.products || [];
        const fromVoucher = (voucherComp?.bestDeals?.length > 0 ? voucherComp.bestDeals : null) || voucherComp?.products || [];
        const combined = [...fromAirtime, ...fromVoucher];

        const isEezi = (p: any) => {
          const name = (p.productName || p.name || '').toLowerCase();
          const group = (p.flash_product_group || p.metadata?.flash_product_group || '').toLowerCase();
          return name.includes('eezi') || group.includes('eezi');
        };

        const seen = new Set<string>();
        const eeziProducts = combined
          .filter(isEezi)
          .filter((p: any) => {
            const key = String(p.id || p.variantId || p.supplierProductId);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .map((p: any) => ({
            id: p.id || p.variantId || p.supplierProductId,
            variantId: p.id || p.variantId,
            name: p.productName || p.name || 'eeziAirtime Token',
            price: p.minAmount ?? p.price ?? 0,
            supplierCode: (p.supplierCode || p.supplier?.code || 'FLASH').toUpperCase(),
            denominations: p.predefinedAmounts || p.denominations || [],
            minAmount: p.minAmount ?? p.price ?? 0,
            maxAmount: p.maxAmount ?? p.price ?? 0,
          }));
        setEeziPinProducts(eeziProducts);
      })
      .catch(() => {/* silently ignore */});
  }, []);

  const loadBeneficiaries = async (): Promise<Beneficiary[]> => {
    try {
      setLoadingState('loading');
      // Single call: airtime and data both use airtime-data service; no need to call twice
      const airtimeDataList = await beneficiaryService.getBeneficiaries('airtime');

      // Banking-grade: one display row per (identifier + network) so same number with different
      // networks (e.g. eeziAirtime vs Global Airtime) are shown as separate selectable recipients
      const normalizeNetwork = (network: string | null | undefined): string => {
        if (!network) return 'unknown';
        const n = String(network).toLowerCase().trim();
        const map: Record<string, string> = {
          vodacom: 'vodacom', mtn: 'mtn', cellc: 'cellc', 'cell c': 'cellc',
          telkom: 'telkom', eeziairtime: 'eeziairtime', 'eezi airtime': 'eeziairtime',
          global: 'global', 'global airtime': 'global', 'global data': 'global'
        };
        return map[n] || n;
      };

      const expanded: AirtimeDataBeneficiary[] = [];
      const seenKey = new Set<string>();

      (airtimeDataList as any[]).forEach((b: any) => {
        const accounts = b.accounts || [];
        const hasUnifiedAccounts = accounts.length > 0;

        if (hasUnifiedAccounts) {
          (accounts as any[]).forEach((acc: any) => {
            if (acc.type !== 'airtime' && acc.type !== 'data') return;
            const identifier = String(acc.identifier || b.identifier || '').trim();
            if (!identifier) return;
            const network = normalizeNetwork(acc.metadata?.network || acc.network || b.metadata?.network);
            const key = `${identifier}|${network}`;
            if (seenKey.has(key)) return;
            seenKey.add(key);
            const networkLabel = (acc.metadata?.network || acc.network || b.metadata?.network || network) as string;
            const displayName = b.name
              ? (networkLabel ? `${b.name} (${networkLabel})` : b.name)
              : (acc.metadata?.label || acc.label || 'Recipient');
            expanded.push({
              id: `${b.id}-${acc.id}`,
              name: displayName,
              identifier,
              accountType: 'airtime',
              metadata: {
                ...(b.metadata || {}),
                network: networkLabel,
                _beneficiaryId: b.id,
                _accountId: acc.id
              },
              lastPaidAt: b.lastPaidAt,
              timesPaid: (b.timesPaid ?? 0),
              createdAt: b.createdAt,
              updatedAt: b.updatedAt,
              accounts: [acc],
              serviceAccountRecords: b.serviceAccountRecords,
              vasServices: b.vasServices
            } as AirtimeDataBeneficiary);
          });
        } else {
          const identifier = String(b.identifier || '').trim();
          if (!identifier) return;
          const network = normalizeNetwork(b.metadata?.network);
          const key = `${identifier}|${network}`;
          if (seenKey.has(key)) return;
          seenKey.add(key);
          expanded.push({
            ...(b as AirtimeDataBeneficiary),
            accountType: 'airtime',
          });
        }
      });

      // Stable sort: most recently updated first, then by name
      expanded.sort((a, b) => {
        const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        if (tb !== ta) return tb - ta;
        return (a.name || '').localeCompare(b.name || '');
      });

      setBeneficiaries(expanded);
      setLoadingState('idle');
      return expanded;
    } catch (err) {
      console.error('Failed to load beneficiaries:', err);
      setError('Failed to load beneficiaries');
      setLoadingState('error');
      return [];
    }
  };

  const handleBeneficiarySelect = (beneficiary: any, accountId?: number): void => {
    const normalized = {
      ...(beneficiary as any),
      id: beneficiary.id != null ? String(beneficiary.id) : ''
    } as Beneficiary;
    
    // Check if beneficiary has multiple accounts
    const beneficiaryAny = normalized as any;
    const accounts = beneficiaryAny.accounts || [];
    const hasMultipleAccounts = accounts.length > 1;
    
    // If multiple accounts and no accountId specified, show account selector modal
    if (hasMultipleAccounts && !accountId) {
      setPendingBeneficiary(normalized);
      setShowAccountSelector(true);
      return;
    }
    
    // Otherwise, proceed with selection
    void (async () => {
      try {
        setLoadingState('loading');
        setCurrentStep('loading');
        setSelectedBeneficiary(normalized);
        setSelectedAccountId(accountId || null);
        setNetworkPrefixWarning(null);
      const normalizeNetwork = normalizeNetworkName;
      
      // Get beneficiary network from metadata or service accounts
      // STRICT: For PINless top-up, we MUST filter by network - only show products for the beneficiary's network
      let beneficiaryNetwork: string | null = null;
      const allNetworks: string[] = [];
      
      // Try multiple sources for network information
      const beneficiaryAny = normalized as any;
      
      // 1. Check metadata.network
      if (beneficiary.metadata?.network) {
        allNetworks.push(beneficiary.metadata.network);
      }
      
      // 2. Check vasServices (legacy format)
      if (beneficiaryAny.vasServices) {
        const airtimeServices = beneficiaryAny.vasServices.airtime || [];
        const dataServices = beneficiaryAny.vasServices.data || [];
        [...airtimeServices, ...dataServices].forEach((s: any) => {
          if (s.network) allNetworks.push(s.network);
        });
      }
      
      // 3. Check serviceAccountRecords (new unified format)
      if (beneficiaryAny.serviceAccountRecords && Array.isArray(beneficiaryAny.serviceAccountRecords)) {
        beneficiaryAny.serviceAccountRecords
          .filter((acc: any) => acc.serviceType === 'airtime' || acc.serviceType === 'data')
          .forEach((acc: any) => {
            if (acc.serviceData?.network) allNetworks.push(acc.serviceData.network);
          });
      }
      
      // 4. Check accounts (alternative format - most likely for unified beneficiaries)
      // CRITICAL: If accountId is provided, only use that specific account's network
      if (accountId && beneficiaryAny.accounts && Array.isArray(beneficiaryAny.accounts)) {
        const selectedAccount = beneficiaryAny.accounts.find((acc: any) => acc.id === accountId);
        if (selectedAccount) {
          // Use the selected account's network for filtering
          if (selectedAccount.metadata?.network) {
            beneficiaryNetwork = normalizeNetwork(selectedAccount.metadata.network);
          } else if (selectedAccount.network) {
            beneficiaryNetwork = normalizeNetwork(selectedAccount.network);
          }
        }
      } else if (beneficiaryAny.accounts && Array.isArray(beneficiaryAny.accounts)) {
        // No specific account selected - check all accounts
        beneficiaryAny.accounts
          .filter((acc: any) => acc.type === 'airtime' || acc.type === 'data')
          .forEach((acc: any) => {
            if (acc.metadata?.network) allNetworks.push(acc.metadata.network);
            if (acc.network) allNetworks.push(acc.network);
          });
      }
      
      // Get unique networks (only if we haven't already set beneficiaryNetwork from selected account)
      let uniqueNetworks: string[] = [];
      if (!beneficiaryNetwork) {
        uniqueNetworks = [...new Set(allNetworks.map(n => normalizeNetwork(n)).filter(Boolean))];
        
        // If beneficiary has exactly one network, use it for filtering
        if (uniqueNetworks.length === 1) {
          beneficiaryNetwork = uniqueNetworks[0];
        } else if (uniqueNetworks.length > 1) {
          // Multiple networks - show all products
          console.warn('⚠️ Beneficiary has multiple networks:', uniqueNetworks, '- Will show all products');
          beneficiaryNetwork = null; // Show all if multiple networks
        }
      }
      
      // Global Airtime (Flash): use backend overlay catalog for international products
      const isGlobalAirtimeBeneficiary = (beneficiaryNetwork === 'global');
      
      if (isGlobalAirtimeBeneficiary) {
        console.log('🌐 Global Airtime beneficiary detected — fetching international catalog from backend');
        setNetworkPrefixWarning(null);
        try {
          const rawBeneficiaryId = beneficiaryAny.metadata?._beneficiaryId || normalized.id;
          const catalogData = await airtimeDataService.getCatalog(String(rawBeneficiaryId));
          setCatalog(catalogData);
          setCurrentStep('catalog');
          setLoadingState('idle');
        } catch (catalogErr) {
          console.error('❌ Failed to load international catalog:', catalogErr);
          setError('Failed to load international airtime products. Please try again.');
          setLoadingState('error');
          setCurrentStep('beneficiary');
        }
        return;
      }
      
      // Prefer MSISDN prefix over saved network label for pinless product filtering
      const msisdnForNetwork = normalized.identifier || '';
      const prefixNetwork = getNetworkFromMsisdn(msisdnForNetwork);
      const normalizedPrefixNetwork = prefixNetwork ? normalizeNetwork(prefixNetwork) : null;
      const labelWarning = getBeneficiaryNetworkLabelWarning(msisdnForNetwork, beneficiaryNetwork);
      setNetworkPrefixWarning(labelWarning);

      const effectiveNetwork =
        normalizedPrefixNetwork ||
        (beneficiaryNetwork ? normalizeNetwork(beneficiaryNetwork) : null);
      
      // Load and normalise all airtime/data products via shared helper
      const { airtime: allAirtimeProds, data: allDataProds, globalPin: pinProds } =
        await apiService.getAirtimeDataProducts();

      setGlobalPinProducts(pinProds);

      // Filter by effective network (prefix-first) when known
      const airtimeProds = allAirtimeProds
        .filter((p: any) => {
          if (!effectiveNetwork) return true;
          return extractProductNetwork(p) === effectiveNetwork;
        });

      const dataProds = allDataProds
        .filter((p: any) => {
          if (!effectiveNetwork) return true;
          return extractProductNetwork(p) === effectiveNetwork;
        });

      const catalogData: AirtimeDataCatalog = {
        beneficiary: {
          id: beneficiary.id.toString(),
          label: beneficiary.name,
          identifier: beneficiary.identifier || '',
          network: effectiveNetwork || beneficiaryNetwork || undefined
        },
        products: [...airtimeProds, ...dataProds],
        providers: ['MTN', 'Vodacom', 'CellC', 'Telkom', 'eeziAirtime', 'Global']
      };
      
      setCatalog(catalogData);
      setCurrentStep('catalog');
      setLoadingState('idle');
      } catch (err) {
        console.error('Failed to load catalog:', err);
        setError('Failed to load product catalog');
        setLoadingState('error');
        setCurrentStep('beneficiary');
      }
    })();
  };

  const handleProductSelect = (product: AirtimeDataProduct, selectedDenomCents?: number) => {
    if (selectedBeneficiary && !assertProductMatchesBeneficiaryNetwork(selectedBeneficiary, product)) {
      return;
    }

    const denoms: number[] = Array.isArray((product as any).denominations) ? (product as any).denominations : [];
    const isVariable: boolean = (product as any).isVariable === true;

    // Variable product (own amount) — expand inline input instead of going to confirm
    if (!selectedDenomCents && isVariable) {
      if (expandedProductId === product.id) {
        setExpandedProductId(null);
      } else {
        setExpandedProductId(product.id);
        setInlineAmountInput('');
      }
      return;
    }

    // Multi-denom fixed product — expand denomination picker
    if (!selectedDenomCents && denoms.length > 1) {
      setExpandedProductId(expandedProductId === product.id ? null : product.id);
      return;
    }

    // Resolve the actual price in rands from the chosen denomination (cents) or the product price
    const resolvedProduct: AirtimeDataProduct = selectedDenomCents
      ? {
          ...product,
          price: selectedDenomCents / 100,
          size: `R${(selectedDenomCents / 100).toFixed(0)}`,
        }
      : product;

    setSelectedProduct(resolvedProduct);
    setExpandedProductId(null);
    setInlineAmountInput('');

    // If no beneficiary selected, show option to send to new recipient
    if (!selectedBeneficiary) {
      setShowSendToNewRecipient(true);
    } else {
      prepareProceed();
    }
  };

  const handleInlineAmountConfirm = (product: AirtimeDataProduct) => {
    if (selectedBeneficiary && !assertProductMatchesBeneficiaryNetwork(selectedBeneficiary, product)) {
      return;
    }

    const amount = parseFloat(inlineAmountInput);
    const minR = ((product as any).minAmount || 0) / 100;
    const maxR = ((product as any).maxAmount || 9999) / 100;
    if (!amount || amount < minR || amount > maxR) return;

    const resolvedProduct: AirtimeDataProduct = {
      ...product,
      price: amount,
      size: `R${amount.toFixed(0)}`,
    };

    setSelectedProduct(resolvedProduct);
    setExpandedProductId(null);
    setInlineAmountInput('');

    if (!selectedBeneficiary) {
      setShowSendToNewRecipient(true);
    } else {
      prepareProceed();
    }
  };

  // handleOwnAirtimeAmount / handleOwnDataAmount removed — variable products from the catalog
  // render their own inline amount input via the expandedProductId mechanism

  const handleSendToNewRecipient = async () => {
    if (!newRecipientPhone || !newRecipientName || !selectedProduct) return;
    
    try {
      setLoadingState('loading');
      setError('');
      
      // Validate phone number
      if (!validateMobileNumber(newRecipientPhone)) {
        setError('Please enter a valid South African mobile number');
        setLoadingState('error');
        return;
      }

      const productNetwork = extractProductNetwork(selectedProduct);
      const recipientMismatch = getMsisdnNetworkMismatch(newRecipientPhone, productNetwork);
      if (recipientMismatch.mismatch && recipientMismatch.message) {
        showNetworkMismatchModal(recipientMismatch.message);
        setLoadingState('idle');
        return;
      }
      
      // Create beneficiary first
      const network = selectedProduct.provider || 'Vodacom';
      const newBeneficiary = await centralizedBeneficiaryService.createOrUpdateBeneficiary({
        name: newRecipientName,
        serviceType: selectedProduct.type === 'airtime' ? 'airtime' : 'data',
        serviceData: {
          mobileNumber: newRecipientPhone,
          network: network
        }
      });
      
      // Store for save prompt after purchase
      setLastTransactionBeneficiary({
        name: newRecipientName,
        phone: newRecipientPhone,
        network: network
      });
      
      // Set as selected beneficiary and proceed with purchase
      // Normalize id to string to satisfy overlay Beneficiary type
      const normalizedNew = {
        ...(newBeneficiary as any),
        id: newBeneficiary.id != null ? String(newBeneficiary.id) : ''
      } as Beneficiary;
      setSelectedBeneficiary(normalizedNew);
      setShowSendToNewRecipient(false);

      // Open the universal proceed modal instead of calling the purchase
      // handler directly — preserves the single pre-debit confirm step for
      // new-recipient airtime/data sends too.
      prepareProceed();
      
    } catch (err: any) {
      console.error('Failed to create beneficiary or purchase:', err);
      setError(err.response?.data?.message || err.message || 'Failed to send. Please try again.');
      setLoadingState('error');
    }
  };

  // Build the proceed payload from the currently selected beneficiary + product
  // and open the universal confirm modal. Replaces the legacy ConfirmSheet step
  // and the handleSendToNewRecipient bypass that called handleConfirmTransaction
  // directly without a final confirmation.
  const prepareProceed = () => {
    if (!selectedBeneficiary || !selectedProduct) return;
    const isData = selectedProduct.type === 'data';
    const notices: PaymentProceedPayload['notices'] = [];
    if (isData) {
      notices.push({
        type: 'info',
        title: 'Data bundle',
        description: `Valid for ${selectedProduct.validity || '30 days'}.`,
      });
    }
    notices.push({
      type: 'info',
      title: 'Instant delivery',
      description: 'Your airtime/data will be delivered instantly.',
    });
    setProceedPayload({
      amountCents: randsToCents(selectedProduct.price),
      recipientLabel: selectedBeneficiary.name,
      recipientSubLabel: selectedBeneficiary.identifier
        ? `••••${String(selectedBeneficiary.identifier).slice(-4)}`
        : undefined,
      contextLabel: isData ? 'Data bundle purchase' : 'Airtime purchase',
      notices,
      title: isData ? 'Confirm data purchase' : 'Confirm airtime purchase',
      proceedButtonText: 'Yes, buy now',
    });
    setShowProceedModal(true);
  };

  const cancelProceed = () => {
    setProceedPayload(null);
    setShowProceedModal(false);
  };

  const handleConfirmTransaction = async () => {
    if (!selectedBeneficiary || !selectedProduct) return;

    if (!assertProductMatchesBeneficiaryNetwork(selectedBeneficiary, selectedProduct)) {
      return;
    }

    try {
      setLoadingState('loading');
      setError('');
      setShowProceedModal(false);
      setProceedPayload(null);
      
      const idempotencyKey = generateIdempotencyKey();
      
      // Resolve "own" product (airtime_own_* / data_own_*) to a catalog variant when possible
      let productToUse = selectedProduct;
      const isOwnProduct = typeof selectedProduct.id === 'string' && /^(airtime|data)_own_/.test(selectedProduct.id);
      if (isOwnProduct && catalog?.products?.length) {
        const match = catalog.products.find(
          (p: any) => p.type === selectedProduct.type && (
            Math.abs((p.price ?? 0) - selectedProduct.price) < 0.02 ||
            (selectedProduct.type === 'airtime' && Array.isArray(p.denominations) && p.denominations.includes(Math.round(selectedProduct.price * 100))) ||
            (selectedProduct.type === 'data' && p.minAmount != null && Math.abs((p.minAmount / 100) - selectedProduct.price) < 0.02)
          )
        );
        if (match && (match as any).variantId) {
          productToUse = { ...selectedProduct, ...match, variantId: (match as any).variantId } as AirtimeDataProduct;
        } else if (match) {
          productToUse = { ...selectedProduct, ...match };
        }
      }
      
      // Determine productId format for purchase endpoint
      // Backend supports two formats:
      // 1. Numeric variantId (from ProductVariant table - preferred for bestDeals)
      // 2. Old string format: type_supplier_productCode_amount (for legacy VasProduct)
      let productIdForPurchase: string | number;
      
      // CRITICAL: If variantId exists (from bestDeals/ProductVariant), use it directly
      if ((productToUse as any).variantId) {
        productIdForPurchase = (productToUse as any).variantId;
        console.log('✅ Using variantId for purchase:', productIdForPurchase);
      } else if ((productToUse as any).supplierCode && (productToUse as any).supplierProductId) {
        const amountInCents = Math.round(productToUse.price * 100);
        productIdForPurchase = `${(productToUse as any).vasType || productToUse.type}_${(productToUse as any).supplierCode}_${(productToUse as any).supplierProductId}_${amountInCents}`;
        console.log('⚠️ Using legacy string format for purchase:', productIdForPurchase);
      } else if (isOwnProduct) {
        setLoadingState('error');
        const msg = 'This amount isn\'t available for this recipient. Please pick a product from the list, or try a different amount.';
        setError(msg);
        setErrorModalTitle('Amount not available');
        setErrorModalMessage(msg);
        setErrorModalType('error');
        setCurrentStep('catalog'); // Close confirm sheet so only the error modal is visible
        setShowErrorModal(true);
        return;
      } else {
        productIdForPurchase = productToUse.id;
        console.warn('⚠️ Using product.id as fallback:', productIdForPurchase);
      }
      
      // Log purchase request data for debugging
      const realBeneficiaryId = (selectedBeneficiary as any).metadata?._beneficiaryId ?? selectedBeneficiary.id;
      const purchaseData = {
        beneficiaryId: String(realBeneficiaryId),
        productId: String(productIdForPurchase),
        amount: selectedProduct.price,
        idempotencyKey: String(idempotencyKey)
      };
      console.log('📤 Sending purchase request:', purchaseData);
      
      const result = await airtimeDataService.purchase(purchaseData);
      
      // Extract reference from result
      const reference = result?.reference || idempotencyKey;
      const beneficiaryIsMyMoolah = result?.beneficiaryIsMyMoolahUser || false;
      
      setTransactionRef(reference);
      setBeneficiaryIsMyMoolahUser(beneficiaryIsMyMoolah);
      setLoadingState('success');
      setShowSuccess(true);
      
      // If this was a new recipient, show save prompt (beneficiary already saved, just confirm)
      if (lastTransactionBeneficiary) {
        setShowSaveRecipientPrompt(true);
      }
      
      // Reload beneficiaries to update the list
      await loadBeneficiaries();
    } catch (err: any) {
      console.error('Purchase failed:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status
      });
      
      // Check if error response contains transaction data (partial success)
      const errorResponse = err.response?.data;
      if (errorResponse?.data?.reference) {
        // Transaction might have succeeded but error occurred after
        setTransactionRef(errorResponse.data.reference);
        setBeneficiaryIsMyMoolahUser(errorResponse.data.beneficiaryIsMyMoolahUser || false);
        setLoadingState('success');
        setShowSuccess(true);
      } else {
        // Check if there's an alternative product available for automatic retry
        if (errorResponse?.alternativeProduct && errorResponse?.autoRetry) {
          console.log('🔄 Automatic retry with alternative product:', errorResponse.alternativeProduct);
          
          // Store alternative product for automatic retry
          setAlternativeProduct(errorResponse.alternativeProduct);
          
          // Automatically retry with alternative product
          try {
            setLoadingState('loading');
            setError('');
            
            const retryIdempotencyKey = generateIdempotencyKey();
            const retryRealBeneficiaryId = (selectedBeneficiary as any)?.metadata?._beneficiaryId ?? selectedBeneficiary?.id;
            const retryPurchaseData = {
              beneficiaryId: String(retryRealBeneficiaryId),
              productId: String(errorResponse.alternativeProduct.variantId || errorResponse.alternativeProduct.productId),
              amount: errorResponse.alternativeProduct.amount || errorResponse.alternativeProduct.price / 100,
              idempotencyKey: retryIdempotencyKey
            };
            
            console.log('🔄 Retrying purchase with alternative:', retryPurchaseData);
            const retryResult = await airtimeDataService.purchase(retryPurchaseData);
            
            // Success with alternative
            const reference = retryResult?.reference || retryIdempotencyKey;
            setTransactionRef(reference);
            setBeneficiaryIsMyMoolahUser(retryResult?.beneficiaryIsMyMoolahUser || false);
            setLoadingState('success');
            setShowSuccess(true);
            
            // Show success message indicating alternative was used
            setErrorModalTitle('Purchase Successful');
            setErrorModalMessage(`The original product was unavailable, but we successfully processed your purchase using an alternative: ${errorResponse.alternativeProduct.productName} from ${errorResponse.alternativeProduct.supplierName}.`);
            setErrorModalType('info');
            setShowErrorModal(true);
            
            await loadBeneficiaries();
          } catch (retryErr: any) {
            console.error('Automatic retry also failed:', retryErr);
            // Show error with alternative suggestion
            setErrorModalTitle('Product Unavailable');
            setErrorModalMessage(errorResponse?.message || err.response?.data?.message || err.message || 'Purchase failed. Please try again.');
            setErrorModalType('error');
            setAlternativeProduct(errorResponse?.alternativeProduct || null);
            setShowErrorModal(true);
            setLoadingState('error');
            // Close confirm modal when showing error
            setCurrentStep('catalog');
          }
        } else {
          // KYC tier block — show the restricted-wallet-credit explainer modal
          // (with Verify ID CTA) instead of a generic purchase-failed error.
          const kycBlock = parseKycTierError(err);
          if (kycBlock) {
            setRestrictedModalMessage(kycBlock.message);
            setShowRestrictedModal(true);
            setError(kycBlock.message);
            setLoadingState('error');
            setCurrentStep('catalog');
            return;
          }
          // Regular error - show error modal
          // Backend sends error in both 'error' and 'message' fields, prioritize 'message' as it's more user-friendly
          const errorMessage = errorResponse?.message || errorResponse?.error || err.message || 'Purchase failed. Please try again.';
          setErrorModalTitle('Purchase Failed');
          setErrorModalMessage(errorMessage);
          setErrorModalType('error');
          setAlternativeProduct(errorResponse?.alternativeProduct || null);
          setShowErrorModal(true);
          setError(errorMessage);
          setLoadingState('error');
          // Close confirm modal when showing error
          setCurrentStep('catalog');
        }
      }
    }
  };

  const handleAddNewBeneficiary = () => {
    setEditingBeneficiary(null);
    setShowBeneficiaryModal(true);
  };

  const handleBeneficiaryCreated = async (newBeneficiary: Beneficiary) => {
    if (editingBeneficiary) {
      // Update existing beneficiary in the list
      setBeneficiaries(prev => prev.map(b => 
        b.id === editingBeneficiary.id ? newBeneficiary : b
      ));
    } else {
      // Add the new beneficiary to the list
      setBeneficiaries(prev => [...prev, newBeneficiary]);
    }
    setShowBeneficiaryModal(false);
    setEditingBeneficiary(null);

    // Reload beneficiaries from backend so we get full accounts[] + metadata.network
    // for the new unified airtime/data service account. Then select the freshly
    // created beneficiary so network-based filtering works immediately.
    const refreshed = await loadBeneficiaries();
    const newId = String(newBeneficiary.id);
    const hydrated = refreshed.find((b) => String(b.id) === newId);
    if (hydrated) {
      setSelectedBeneficiary(hydrated);
    }
  };

  const handleEditBeneficiary = (beneficiary: any): void => {
    const normalized = {
      ...(beneficiary as any),
      id: beneficiary.id != null ? String(beneficiary.id) : ''
    } as Beneficiary;
    setEditingBeneficiary(normalized);
    setShowBeneficiaryModal(true);
  };

  const handleRemoveBeneficiary = (beneficiary: any): void => {
    const normalized = {
      ...(beneficiary as any),
      id: beneficiary.id != null ? String(beneficiary.id) : ''
    } as Beneficiary;
    setBeneficiaryToRemove(normalized);
    setShowConfirmationModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!beneficiaryToRemove) return;
    
    try {
      const meta = (beneficiaryToRemove as any).metadata;
      const realBeneficiaryId = meta?._beneficiaryId ?? beneficiaryToRemove.id;
      const accounts = (beneficiaryToRemove as any).accounts || [];

      // Banking-grade: Remove only the specific service account when this is an expanded row
      // (one recipient = one account, e.g. eeziAirtime vs Global for same number). Otherwise remove all airtime-data.
      if (meta?._accountId != null && accounts.length === 1) {
        const serviceType = accounts[0].type === 'data' ? 'data' : 'airtime';
        await unifiedBeneficiaryService.removeServiceFromBeneficiary(
          String(realBeneficiaryId),
          serviceType,
          String(meta._accountId ?? accounts[0].id)
        );
      } else {
        await beneficiaryService.removeAllServicesOfType(realBeneficiaryId, 'airtime-data');
      }

      setBeneficiaries((prev) => prev.filter((b) => b.id !== beneficiaryToRemove.id));
      if (selectedBeneficiary?.id === beneficiaryToRemove.id) {
        setSelectedBeneficiary(null);
        setSelectedAccountId(null);
      }

      await loadBeneficiaries();
      setBeneficiaryToRemove(null);
      setShowConfirmationModal(false);
    } catch (error) {
      console.error('Failed to remove beneficiary services:', error);
      alert('Failed to remove recipient. Please try again.');
    }
  };

  const handleRemoveAccount = async (accountId: number) => {
    if (!pendingBeneficiary) return;
    
    try {
      // Find the account to get its type and real service account ID
      const accountToRemove = ((pendingBeneficiary as any).accounts || []).find(
        (acc: any) => acc.id === accountId
      );
      
      if (!accountToRemove) {
        console.error('Account not found:', accountId);
        alert('Account not found. Please try again.');
        return;
      }
      
      // Determine service type from account type ('airtime' or 'data')
      const serviceType = accountToRemove.type === 'airtime' ? 'airtime' : 'data';
      
      // Get the real service account ID
      // If account.id is from backend accounts array, it's the real ID
      // If it's computed (legacy.id * 1000 + ...), we need to find it from serviceAccountRecords
      let realServiceAccountId: string | null = null;
      
      const beneficiaryAny = pendingBeneficiary as any;
      
      // Check if we have serviceAccountRecords (new unified format)
      if (beneficiaryAny.serviceAccountRecords && Array.isArray(beneficiaryAny.serviceAccountRecords)) {
        const matchingRecord = beneficiaryAny.serviceAccountRecords.find((rec: any) => {
          return rec.serviceType === serviceType && 
                 (rec.serviceData?.mobileNumber === accountToRemove.identifier ||
                  rec.serviceData?.msisdn === accountToRemove.identifier ||
                  rec.serviceData?.network === accountToRemove.metadata?.network);
        });
        
        if (matchingRecord && matchingRecord.id) {
          realServiceAccountId = String(matchingRecord.id);
        }
      }
      
      // If we couldn't find it in serviceAccountRecords, check if account.id is the real ID
      // (accounts from backend should have real IDs)
      if (!realServiceAccountId) {
        // Check if account.id looks like a computed ID (very large number)
        // Real service account IDs are typically smaller
        if (accountId < 1000000) {
          // Likely a real service account ID
          realServiceAccountId = String(accountId);
        } else {
          // Computed ID - we need to fetch the beneficiary services to get real IDs
          try {
            const beneficiaryServices = await unifiedBeneficiaryService.getBeneficiaryServices(
              String(pendingBeneficiary.id)
            );
            const services = (beneficiaryServices as any).serviceAccountRecords || [];
            const matchingService = services.find((s: any) => {
              return s.serviceType === serviceType &&
                     (s.serviceData?.mobileNumber === accountToRemove.identifier ||
                      s.serviceData?.msisdn === accountToRemove.identifier);
            });
            
            if (matchingService && matchingService.id) {
              realServiceAccountId = String(matchingService.id);
            }
          } catch (fetchError) {
            console.error('Failed to fetch beneficiary services:', fetchError);
          }
        }
      }
      
      if (!realServiceAccountId) {
        console.error('Could not determine real service account ID for account:', accountToRemove);
        alert('Could not find service account. Please try again.');
        return;
      }
      
      // Remove the specific service account from the beneficiary
      await unifiedBeneficiaryService.removeServiceFromBeneficiary(
        String(pendingBeneficiary.id),
        serviceType,
        realServiceAccountId
      );
      
      // Reload beneficiaries to reflect the change
      const updatedBeneficiaries = await loadBeneficiaries();
      
      // Find the updated beneficiary in the reloaded list
      const updatedBeneficiary = updatedBeneficiaries.find(
        (b: any) => String(b.id) === String(pendingBeneficiary.id)
      );
      
      // If the removed account was the only one, close the modal
      const remainingAccounts = (updatedBeneficiary as any)?.accounts || [];
      
      if (remainingAccounts.length === 0) {
        // No accounts left, close the modal
        setShowAccountSelector(false);
        setPendingBeneficiary(null);
      } else if (remainingAccounts.length === 1) {
        // Only one account left, auto-select it and close modal
        setShowAccountSelector(false);
        if (updatedBeneficiary) {
          handleBeneficiarySelect(updatedBeneficiary, remainingAccounts[0].id);
        }
        setPendingBeneficiary(null);
      } else {
        // Multiple accounts remain, update the pending beneficiary with fresh data
        if (updatedBeneficiary) {
          setPendingBeneficiary(updatedBeneficiary as any);
          // Modal stays open with updated account list
        } else {
          // Beneficiary not found (shouldn't happen), close modal
          setShowAccountSelector(false);
          setPendingBeneficiary(null);
        }
      }
    } catch (error) {
      console.error('Failed to remove account:', error);
      alert('Failed to remove number. Please try again.');
    }
  };

  const handleBrowseProducts = async () => {
    try {
      setLoadingState('loading');
      // Load and normalise all airtime/data products via shared helper
      const { airtime: airtimeProds, data: dataProds, globalPin: pinProds2 } =
        await apiService.getAirtimeDataProducts();

      setGlobalPinProducts(pinProds2);

      const catalogData: AirtimeDataCatalog = {
        beneficiary: { id: '', label: '', identifier: '' },
        products: [...airtimeProds, ...dataProds],
        providers: ['MTN', 'Vodacom', 'CellC', 'Telkom', 'eeziAirtime', 'Global']
      };
      
      setCatalog(catalogData);
      setCurrentStep('catalog');
      setLoadingState('idle');
    } catch (err) {
      console.error('Failed to load products:', err);
      setError('Failed to load products');
      setLoadingState('error');
    }
  };

  const handleGoHome = () => {
    setShowSuccess(false);
    navigate('/');
  };

  const handleDoAnotherTransaction = () => {
    setShowSuccess(false);
    setSelectedBeneficiary(null);
    setSelectedAccountId(null);
    setSelectedProduct(null);
    setCatalog(null);
    setCurrentStep('beneficiary');
    setTransactionRef('');
    setShowSendToNewRecipient(false);
    setNewRecipientPhone('');
    setNewRecipientName('');
    setShowSaveRecipientPrompt(false);
    setLastTransactionBeneficiary(null);
  };

  const getSummaryRows = () => {
    if (!selectedBeneficiary || !selectedProduct) return [];

    // Build a clean single description line, e.g. "Vodacom Airtime · R2–R999"
    // Avoid repeating the network name that's already in the product name.
    const productLabel = selectedProduct.name; // e.g. "Vodacom Airtime"
    const sizeLabel = selectedProduct.size;    // e.g. "R200" or "R2–R999"

    return [
      {
        label: 'To',
        value: `${selectedBeneficiary.name} · ${selectedBeneficiary.identifier}`
      },
      {
        label: selectedProduct.type === 'data' ? 'Data Bundle' : 'Airtime',
        value: `${productLabel} · ${sizeLabel}`
      },
      {
        label: 'Amount',
        value: formatCurrency(selectedProduct.price),
        highlight: true
      }
    ];
  };

  const getConfirmNotices = () => {
    const notices = [];
    
    if (selectedProduct?.type === 'data') {
      notices.push({
        id: 'data-validity',
        type: 'info' as const,
        title: 'Data Bundle',
        description: `Valid for ${selectedProduct.validity || '30 days'}`
      });
    }
    
    notices.push({
      id: 'instant-delivery',
      type: 'info' as const,
      title: 'Instant Delivery',
      description: 'Your airtime/data will be delivered instantly'
    });
    
    return notices;
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem', position: 'relative' }}>
        <Button
          variant="ghost"
          onClick={() => navigate('/transact')}
          style={{
            padding: '8px',
            minWidth: '44px',
            minHeight: '44px',
            position: 'absolute',
            left: '0',
            zIndex: 1
          }}
        >
          <ArrowLeft style={{ width: '20px', height: '20px' }} />
        </Button>
        
        <div style={{ 
          flex: 1, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            Airtime & Data
          </h1>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '12px',
            color: '#6b7280',
            margin: 0
          }}>
            Top-up airtime and data instantly.
          </p>
        </div>
      </div>

      {/* Step 1: Beneficiary Selection */}
      {currentStep === 'beneficiary' && (
        <div className="space-y-4">
          <BeneficiaryList
            type="airtime"
            beneficiaries={beneficiaries}
            selectedBeneficiary={selectedBeneficiary}
            selectedAccountId={selectedAccountId}
            onSelect={handleBeneficiarySelect}
            onAddNew={handleAddNewBeneficiary}
            onEdit={handleEditBeneficiary}
            onRemove={handleRemoveBeneficiary}
            searchPlaceholder="Search recipient name or number"
            isLoading={loadingState === 'loading'}
            showFilters={false}
          />

          {/* eeziAirtime Token — ZAR PIN cash token, always visible */}
          <div
            onClick={() => setShowEeziPinModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#86BE41'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            <div className="flex items-center gap-3">
              <div style={{ width: '40px', height: '40px', backgroundColor: '#86BE41', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Smartphone style={{ width: '20px', height: '20px', color: '#ffffff' }} />
              </div>
              <div>
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                  eeziAirtime
                </p>
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  {eeziPinProducts.length > 0
                    ? `${eeziPinProducts.length} PIN ${eeziPinProducts.length === 1 ? 'option' : 'options'} · Flash · ZAR`
                    : 'PIN cash token · Flash · ZAR'}
                </p>
              </div>
            </div>
            <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: '600', color: '#86BE41', margin: 0 }}>
              Buy PIN →
            </p>
          </div>

          {/* TEMPORARILY HIDDEN: International PIN card — re-enable once pinless flow is confirmed with Flash
          {globalPinProducts.length > 0 && (
            <div
              onClick={() => setShowGlobalPinModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = '#86BE41'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#ffffff'; }}
            >
              <div className="flex items-center gap-3">
                <div style={{ width: '40px', height: '40px', backgroundColor: '#86BE41', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Globe style={{ width: '20px', height: '20px', color: '#ffffff' }} />
                </div>
                <div>
                  <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    International PIN
                  </p>
                  <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    {globalPinProducts.length} PIN options · Flash
                  </p>
                </div>
              </div>
              <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: '600', color: '#86BE41', margin: 0 }}>
                Buy PIN →
              </p>
            </div>
          )}
          */}

          {/* Global Airtime tip hidden — endpoint not yet active */}
        </div>
      )}

      {/* Loading step — shown while catalog is being fetched after beneficiary selection */}
      {currentStep === 'loading' && (
        <BrandSpinner
          style={{ padding: '3rem 1rem' }}
          size={40}
          label={`Loading products for ${selectedBeneficiary?.name || 'recipient'}...`}
        />
      )}

      {/* Step 2: Product Catalog - Allow browsing without beneficiary */}
      {currentStep === 'catalog' && catalog && (
        <div className="space-y-4">
          {/* Selected Recipient Summary - Only show if beneficiary is selected */}
          {selectedBeneficiary && (
            <Card style={{
              backgroundColor: '#f8fafe',
              border: '1px solid #86BE41',
              borderRadius: '12px'
            }}>
              <CardContent style={{ padding: '1rem' }}>
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#86BE41',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Smartphone style={{ width: '20px', height: '20px', color: '#ffffff' }} />
                  </div>
                  <div>
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#1f2937'
                    }}>
                      {selectedBeneficiary.name}
                    </p>
                    <p style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      {selectedBeneficiary.identifier}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedBeneficiary && networkPrefixWarning && (
            <Card style={{
              backgroundColor: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: '12px'
            }}>
              <CardContent style={{ padding: '1rem' }}>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: '#9a3412',
                  margin: 0
                }}>
                  {networkPrefixWarning}
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Show message if no beneficiary selected */}
          {!selectedBeneficiary && (
            <Card style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '12px'
            }}>
              <CardContent style={{ padding: '1rem' }}>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: '#92400e',
                  margin: 0
                }}>
                  💡 Select a product below to send to a new recipient
                </p>
              </CardContent>
            </Card>
          )}

          {/* Airtime Products */}
          <Card style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px'
          }}>
            <CardHeader>
              <CardTitle style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '16px',
                fontWeight: '700',
                color: '#1f2937'
              }}>
                Airtime Products
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <div className="space-y-3">
                {/* Airtime Product List */}
                {catalog.products.filter(product => product.type === 'airtime').map((product, index) => {
                  const supplierKey = (product.supplierCode || '').toUpperCase();
                  const supplierBorder = isUatOrStaging ? (SUPPLIER_BORDER[supplierKey] ?? '1px solid #e2e8f0') : '1px solid #e2e8f0';
                  const denoms: number[] = Array.isArray((product as any).denominations) ? (product as any).denominations : [];
                  const isProductVariable: boolean = (product as any).isVariable === true;
                  const isExpanded = expandedProductId === product.id;
                  const hasMultipleDenoms = denoms.length > 1;
                  const needsPicker = isProductVariable || hasMultipleDenoms;
                  const minR = ((product as any).minAmount || 0) / 100;
                  const maxR = ((product as any).maxAmount || 1000) / 100;
                  const inlineAmountValid = parseFloat(inlineAmountInput) >= minR && parseFloat(inlineAmountInput) <= maxR;
                  return (
                  <div key={`${product.id}_${index}`} style={{ borderRadius: '12px', overflow: 'hidden', border: supplierBorder }}>
                    <div
                      onClick={() => handleProductSelect(product)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: isExpanded ? '#f0fdf4' : '#ffffff',
                      }}
                      onMouseOver={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseOut={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#ffffff'; }}
                    >
                      <div className="flex items-center gap-3">
                        <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {getNetworkIcon((product as any).network || (product as any).provider || '', 40)}
                        </div>
                        <div>
                          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                            {product.name}
                          </p>
                          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280' }}>
                            {isProductVariable
                              ? `R${minR.toFixed(0)}–R${maxR.toFixed(0)} (enter amount)`
                              : hasMultipleDenoms
                                ? `${denoms.length} amounts available`
                                : product.size}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {needsPicker ? (
                          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: '600', color: '#86BE41' }}>
                            {isExpanded ? 'Close ▲' : 'Select ▼'}
                          </p>
                        ) : (
                          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>
                            {formatCurrency(product.price)}
                          </p>
                        )}
                        {product.isBestDeal && (
                          <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '10px', color: '#16a34a', fontWeight: '500' }}>Best Deal</span>
                        )}
                      </div>
                    </div>
                    {/* Inline variable amount input */}
                    {isExpanded && isProductVariable && (
                      <div style={{ padding: '8px 12px 12px', backgroundColor: '#f0fdf4', borderTop: '1px solid #dcfce7' }}>
                        <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                          Enter amount (R{minR.toFixed(0)}–R{maxR.toFixed(0)}):
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder={`R${minR.toFixed(0)}–R${maxR.toFixed(0)}`}
                            value={inlineAmountInput}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setInlineAmountInput(e.target.value.replace(/[^\d.]/g, ''))}
                            style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontFamily: 'Montserrat, sans-serif', fontSize: '14px', color: '#1f2937' }}
                          />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleInlineAmountConfirm(product); }}
                            disabled={!inlineAmountValid}
                            style={{ padding: '8px 16px', backgroundColor: inlineAmountValid ? '#86BE41' : '#9ca3af', color: '#ffffff', border: 'none', borderRadius: '8px', fontFamily: 'Montserrat, sans-serif', fontSize: '14px', fontWeight: '600', cursor: inlineAmountValid ? 'pointer' : 'not-allowed' }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Inline denomination picker */}
                    {isExpanded && hasMultipleDenoms && !isProductVariable && (
                      <div style={{ padding: '8px 12px 12px', backgroundColor: '#f0fdf4', borderTop: '1px solid #dcfce7' }}>
                        <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Select amount:</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {denoms.map((denom: number) => (
                            <button
                              key={denom}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleProductSelect(product, denom); }}
                              style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '13px', fontWeight: '600', padding: '8px 14px', borderRadius: '8px', border: '2px solid #86BE41', background: '#ffffff', color: '#1f2937', cursor: 'pointer', transition: 'all 0.15s ease' }}
                              onMouseOver={(e) => { e.currentTarget.style.background = '#86BE41'; e.currentTarget.style.color = '#fff'; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#1f2937'; }}
                            >
                              {formatCurrency(denom / 100)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Data Products — individual rows with product names, icons, validity */}
          {catalog.products.some(p => p.type === 'data') && (() => {
            const dataProducts = catalog.products.filter(p => p.type === 'data');
            const network = (dataProducts[0] as any)?.network || (dataProducts[0] as any)?.provider || '';
            return (
          <Card style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            <CardHeader style={{ padding: '16px 16px 8px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
              <div style={{ flexShrink: 0 }}>
                {getNetworkIcon(network, 36)}
              </div>
              <div>
                <CardTitle style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1f2937'
                }}>
                  {network} Data Bundles
                </CardTitle>
                <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {dataProducts.length} bundles available
                </p>
              </div>
            </CardHeader>
            
            <CardContent style={{ padding: '8px 12px 12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {dataProducts.map((product, index) => {
                  const p = product as any;
                  const bundleLabel = p.bundleLabel || p.name || 'Data Bundle';
                  const bundleCategory = p.bundleCategory || 'data';
                  const dataSize = p.size || '';
                  const validity = p.validity || '30 Days';
                  const supplierKey = (product.supplierCode || '').toUpperCase();
                  const supplierBorder = isUatOrStaging ? (SUPPLIER_BORDER[supplierKey] ?? 'transparent') : 'transparent';

                  return (
                  <div
                    key={`${product.id}_data_${index}`}
                    onClick={() => handleProductSelect(product)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      border: `1px solid #f0f0f0`,
                      borderLeft: `3px solid ${supplierBorder === 'transparent' ? '#e2e8f0' : supplierBorder.replace('2px solid ', '')}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      backgroundColor: '#ffffff',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.transform = 'translateX(2px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    {/* Category Icon */}
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      backgroundColor: bundleCategory === 'whatsapp' ? '#e7fce7'
                        : bundleCategory === 'tiktok' ? '#f0f0f0'
                        : bundleCategory === 'facebook' ? '#e8f0fe'
                        : bundleCategory === 'youtube' ? '#fee8e8'
                        : bundleCategory === 'lte' ? '#e0f2fe'
                        : bundleCategory === 'allnetwork' ? '#ede9fe'
                        : '#f0f7ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {getCategoryIcon(bundleCategory, 20)}
                    </div>

                    {/* Bundle Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#1f2937',
                        lineHeight: '1.3',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {bundleLabel}
                      </p>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                        {dataSize && (
                          <span style={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#2D8CCA',
                            backgroundColor: '#eff6ff',
                            padding: '1px 6px',
                            borderRadius: '4px',
                          }}>
                            {dataSize}
                          </span>
                        )}
                        <span style={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '11px',
                          color: '#9ca3af',
                        }}>
                          {validity}
                        </span>
                      </div>
                    </div>

                    {/* Price */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <p style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '15px',
                        fontWeight: '700',
                        color: '#1f2937',
                      }}>
                        {formatCurrency(product.price)}
                      </p>
                    </div>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
            );
          })()}

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCurrentStep('beneficiary')}
              style={{
                flex: '1',
                minHeight: '44px',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: '500',
                borderRadius: '12px'
              }}
            >
              Back
            </Button>
          </div>
        </div>
      )}

      {/* Send to New Recipient Form */}
      {showSendToNewRecipient && selectedProduct && (
        <Card style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <CardHeader>
            <CardTitle style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937'
            }}>
              Send to New Recipient
            </CardTitle>
          </CardHeader>
          <CardContent style={{ paddingTop: '1rem' }}>
            <div className="space-y-4">
              <div>
                <label style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#1f2937',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={newRecipientName}
                  onChange={(e) => setNewRecipientName(e.target.value)}
                  placeholder="Enter recipient name"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#1f2937',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={newRecipientPhone}
                  onChange={(e) => setNewRecipientPhone(e.target.value)}
                  placeholder="0821234567"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSendToNewRecipient(false);
                    setSelectedProduct(null);
                  }}
                  style={{
                    flex: '1',
                    minHeight: '44px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '12px'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendToNewRecipient}
                  disabled={!newRecipientName || !newRecipientPhone || loadingState === 'loading'}
                  style={{
                    flex: '1',
                    minHeight: '44px',
                    backgroundColor: '#86BE41',
                    color: '#ffffff',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '12px'
                  }}
                >
                  {loadingState === 'loading' ? 'Processing...' : 'Send'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirm — now rendered as the universal PaymentProceedModal
          overlay (opened by prepareProceed). The legacy inline ConfirmSheet
          step has been removed in favour of a single pre-debit confirm. */}

      {/* Universal pre-debit proceed confirmation. */}
      <PaymentProceedModal
        open={showProceedModal}
        payload={proceedPayload}
        onProceed={handleConfirmTransaction}
        onCancel={cancelProceed}
        isProcessing={loadingState === 'loading'}
      />

      {/* Beneficiary Modal */}
      <BeneficiaryModal
        isOpen={showBeneficiaryModal}
        onClose={() => {
          setShowBeneficiaryModal(false);
          setEditingBeneficiary(null);
        }}
        type="airtime"
        onSuccess={handleBeneficiaryCreated}
        editBeneficiary={editingBeneficiary}
        onAddNumber={() => {
          setShowBeneficiaryModal(false);
          setShowAddNumberModal(true);
        }}
      />

      {/* Add Additional Number Modal */}
      {editingBeneficiary && (
        <AddAdditionalNumberModal
          isOpen={showAddNumberModal}
          onClose={() => {
            setShowAddNumberModal(false);
            // Clear editingBeneficiary when add number modal is closed
            setEditingBeneficiary(null);
          }}
          beneficiaryId={editingBeneficiary.id}
          beneficiaryName={editingBeneficiary.name}
          onSuccess={async () => {
            // Reload beneficiaries to show the new number
            await loadBeneficiaries();
            setShowAddNumberModal(false);
            // Clear editingBeneficiary after successful add
            setEditingBeneficiary(null);
          }}
        />
      )}

      {/* Account Selector Modal - for recipients with multiple numbers */}
      {pendingBeneficiary && (
        <AccountSelectorModal
          isOpen={showAccountSelector}
          onClose={() => {
            setShowAccountSelector(false);
            setPendingBeneficiary(null);
          }}
          recipientName={pendingBeneficiary.name}
          accounts={(pendingBeneficiary as any).accounts || []}
          onSelectAccount={(accountId) => {
            setShowAccountSelector(false);
            // Now call handleBeneficiarySelect with the chosen accountId
            handleBeneficiarySelect(pendingBeneficiary, accountId);
            setPendingBeneficiary(null);
          }}
          onRemoveAccount={handleRemoveAccount}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => {
          setShowConfirmationModal(false);
          setBeneficiaryToRemove(null);
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Recipient"
        message="Are you sure you want to remove"
        confirmText="Yes, remove"
        cancelText="Cancel"
        type="danger"
        beneficiaryName={beneficiaryToRemove?.name}
      />

      {/* Save Recipient Prompt - Show after successful purchase to new recipient */}
      {showSaveRecipientPrompt && lastTransactionBeneficiary && (
        <Dialog open={showSaveRecipientPrompt} onOpenChange={setShowSaveRecipientPrompt}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recipient Saved</DialogTitle>
              <DialogDescription>
                {lastTransactionBeneficiary.name} ({lastTransactionBeneficiary.phone}) has been saved as a recipient. You can send to them again from your recipient list.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveRecipientPrompt(false);
                  setLastTransactionBeneficiary(null);
                }}
                style={{
                  flex: '1',
                  minHeight: '44px'
                }}
              >
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent 
          style={{
            maxWidth: '400px',
            width: '90vw',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: 0,
            border: 'none',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            zIndex: 9999
          }}
          aria-describedby="airtime-data-success-description"
        >
          <div id="airtime-data-success-description" className="sr-only">
            Purchase successful notification
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
            color: '#ffffff',
            padding: '20px 24px',
            borderRadius: '16px 16px 0 0'
          }}>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle style={{ width: '24px', height: '24px' }} />
                <DialogTitle style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#ffffff',
                  margin: 0
                }}>
                  Purchase Successful
                </DialogTitle>
              </div>
              <DialogDescription style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '12px',
                color: '#ffffff',
                opacity: 0.9,
                margin: 0
              }}>
                {new Date().toLocaleString()}
              </DialogDescription>
              {beneficiaryIsMyMoolahUser && (
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '11px',
                  color: '#ffffff',
                  opacity: 0.8,
                  marginTop: '4px',
                  margin: 0
                }}>
                  ✅ Recipient notified via MyMoolah
                </p>
              )}
            </DialogHeader>
          </div>

          <div style={{ padding: '16px 20px' }}>
            {/* Transaction Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div className="flex justify-between" style={{ alignItems: 'center' }}>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  Reference
                </span>
                <span style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: '#6b7280',
                  maxWidth: '60%',
                  textAlign: 'right',
                  wordBreak: 'break-all'
                }}>
                  {transactionRef?.replace(/^overlay_user_/, '') || transactionRef}
                </span>
              </div>
              
              {getSummaryRows().map((row, index) => (
                <div key={index} className="flex justify-between" style={{ alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    {row.label}
                  </span>
                  <span style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '13px',
                    fontWeight: row.highlight ? '700' : '600',
                    color: '#1f2937'
                  }}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2" style={{ marginTop: '16px' }}>
              <Button
                onClick={handleDoAnotherTransaction}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #86BE41 0%, #2D8CCA 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600',
                  height: '40px',
                  padding: '0'
                }}
              >
                Do Another Transaction
              </Button>
              
              <Button
                onClick={handleGoHome}
                variant="outline"
                style={{
                  width: '100%',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '13px',
                  fontWeight: '500',
                  height: '40px',
                  borderRadius: '8px',
                  borderColor: '#e2e8f0',
                  padding: '0'
                }}
              >
                <Home style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                Go to Home
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setAlternativeProduct(null);
        }}
        title={errorModalTitle}
        message={errorModalMessage}
        type={errorModalType}
      />

      {/* Restricted wallet credit modal — shown when a spend is blocked by
          KYC_TIER_REQUIRED (Tier 0 user with a CP restricted credit balance). */}
      <RestrictedWalletCreditModal
        isOpen={showRestrictedModal}
        onClose={() => setShowRestrictedModal(false)}
        blockedMessage={restrictedModalMessage}
      />

      {/* Global PIN Modal — International Airtime, no beneficiary required */}
      {/* Flash charges in ZAR (e.g. ~R21 per $1 face value); wallet debits ZAR */}
      {showGlobalPinModal && (
        <GlobalPinModal
          products={globalPinProducts}
          selectedAccountId={selectedAccountId}
          onClose={() => setShowGlobalPinModal(false)}
          currency="ZAR"
          confirmHint="You pay in Rands (ZAR). A PIN code will be generated instantly. Copy and use it to top-up any international number."
        />
      )}

      {/* eeziAirtime PIN Modal — ZAR cash token, no beneficiary required */}
      {showEeziPinModal && (
        <GlobalPinModal
          products={[]}
          selectedAccountId={selectedAccountId}
          onClose={() => setShowEeziPinModal(false)}
          title="eeziAirtime"
          subtitle="Buy PIN · Copy · Use on any SA network"
          currency="ZAR"
          confirmHint="A PIN cash token will be generated instantly. Dial the USSD from the phone you want to top up."
          ownAmountMode={true}
          minAmountCents={200}
          maxAmountCents={99900}
          onPurchase={(amountCents, idempotencyKey) =>
            apiService.purchaseEeziToken(amountCents, idempotencyKey)
          }
          eeziAirtimeToken={true}
        />
      )}
    </div>
  );
}